import { useState, useCallback, useMemo } from 'react';
import { useGameStore } from '../store/useGameStore';
import { StoneEditor } from './StoneEditor';
import { StoneSearchModal, type SearchFilters } from './StoneSearchModal';
import { SHAPE_DEFS, getShapeCells } from '../data/shapes';
import { COLOR_CSS, COLOR_LABELS, ELEMENT_CSS, ELEMENT_LABELS } from '../data/attributes';
import type { Stone, StoneColor, ElementType, StoneShape } from '../types';
import './RightPanel.css';

function StoneBlock({ stone, onDragStart, onRemove, onRotate }: {
    stone: Stone;
    onDragStart: (stoneId: string) => void;
    onRemove: (stoneId: string) => void;
    onRotate: (stoneId: string) => void;
}) {
    const { placedStones } = useGameStore();
    const isPlaced = !!placedStones[stone.id];
    const colorHex = COLOR_CSS[stone.color];
    const shapeDef = stone.shape === 'TIAN' && stone.type === 'craft'
        ? SHAPE_DEFS.TIAN
        : SHAPE_DEFS[stone.shape];

    // 确定形状预览使用哪个矩阵
    const matrix = shapeDef.rotations[stone.currentRotation % shapeDef.rotations.length];

    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.setData('text/plain', stone.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(stone.id);
    };

    const handleDragEnd = () => {
        onDragStart('');
    };

    const typeLabel = stone.type === 'craft' ? '匠心石' : '机巧石';

    return (
        <div
            className={`stone-block ${isPlaced ? 'placed' : ''} ${stone.color} element-${stone.element}`}
            draggable={!isPlaced}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            style={{
                borderColor: isPlaced ? 'rgba(255,255,255,0.1)' : `${colorHex}66`,
                boxShadow: isPlaced ? 'none' : `0 0 8px ${colorHex}33`,
            }}
        >
            {/* 元素花纹装饰层 (纯装饰, 不包裹内容) */}
            <div className="stone-pattern" />

            <div className="stone-block-header">
                <span className="stone-type-tag" style={{ background: `${colorHex}33`, color: colorHex }}>
                    {typeLabel}
                </span>
                <span className="stone-element-icon">
                    {stone.element === 'fire' ? '🔥' : stone.element === 'ice' ? '❄️' : stone.element === 'wood' ? '🌿' : '⚡'}
                </span>
                <span className="stone-color-label" style={{ color: colorHex }}>● {COLOR_LABELS[stone.color]}</span>
            </div>

            {/* 形状预览 */}
            <div className="stone-shape-preview">
                {matrix.map((row, ri) => (
                    <div key={ri} className="shape-row">
                        {row.map((cell, ci) => (
                            <span
                                key={ci}
                                className={`shape-cell ${cell ? 'filled' : 'empty'}`}
                                style={cell ? { background: colorHex, boxShadow: `0 0 3px ${colorHex}66` } : {}}
                            />
                        ))}
                    </div>
                ))}
            </div>

            {/* 属性列表 */}
            <div className="stone-attrs">
                {stone.attributes.map(attr => (
                    <span key={attr.id} className="stone-attr-tag"
                          style={attr.element ? { borderLeftColor: ELEMENT_CSS[attr.element] } : { borderLeftColor: colorHex }}>
            {attr.element && <span className="attr-elem-icon">
              {attr.element === 'fire' ? '🔥' : attr.element === 'ice' ? '❄️' : attr.element === 'wood' ? '🌿' : '⚡'}
            </span>}
                        {attr.label}
                        {attr.category !== 'resonance' && <span className="attr-val">+{attr.value}</span>}
          </span>
                ))}
            </div>

            {/* 操作按钮 */}
            <div className="stone-actions">
                {!isPlaced && (
                    <button className="stone-action-btn rotate" onClick={() => onRotate(stone.id)} title="旋转">
                        ⟳
                    </button>
                )}
                {isPlaced && <span className="placed-badge">已放置</span>}
                <button className="stone-action-btn remove" onClick={() => onRemove(stone.id)} title="删除">
                    ✕
                </button>
            </div>
        </div>
    );
}

export function RightPanel() {
    const { stones, rotateStone, removeStone, addStone, setDraggingStone } = useGameStore();
    const [showEditor, setShowEditor] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [filter, setFilter] = useState<'all' | 'skill' | 'craft'>('all');
    const [searchFilters, setSearchFilters] = useState<SearchFilters>({
        elements: [],
        types: [],
        colors: [],
        attrLabels: [],
    });
    const [savedFilters, setSavedFilters] = useState<SearchFilters | null>(null);

    const hasActiveFilters = searchFilters.elements.length > 0 || searchFilters.types.length > 0 ||
        searchFilters.colors.length > 0 || searchFilters.attrLabels.length > 0;

    // 打开搜索弹窗时保存当前筛选状态快照
    const openSearch = useCallback(() => {
        setSavedFilters({ ...searchFilters });
        setShowSearch(true);
    }, [searchFilters]);

    // 取消搜索：恢复到打开前的状态
    const cancelSearch = useCallback(() => {
        if (savedFilters) {
            setSearchFilters(savedFilters);
        }
        setSavedFilters(null);
        setShowSearch(false);
    }, [savedFilters]);

    // 确认搜索
    const confirmSearch = useCallback((filters: SearchFilters) => {
        setSearchFilters(filters);
        setSavedFilters(null);
        setShowSearch(false);
    }, []);

    const handleDragStart = useCallback((stoneId: string) => {
        setDraggingStone(stoneId || null);
    }, [setDraggingStone]);

    const handleRemove = useCallback((stoneId: string) => {
        removeStone(stoneId);
    }, [removeStone]);

    const handleRotate = useCallback((stoneId: string) => {
        rotateStone(stoneId);
    }, [rotateStone]);

    // 过滤逻辑：先用顶部类型按钮过滤，再用搜索条件过滤
    const filteredStones = useMemo(() => {
        let result = filter === 'all'
            ? stones
            : stones.filter(s => s.type === filter);

        const { elements, types, colors, attrLabels } = searchFilters;

        // 元素类型过滤
        if (elements.length > 0) {
            result = result.filter(s => elements.includes(s.element));
        }
        // 石头类型过滤（与顶部分类按钮互补，不重复过滤）
        // 颜色过滤
        if (colors.length > 0) {
            result = result.filter(s => colors.includes(s.color));
        }
        // 属性标签过滤
        if (attrLabels.length > 0) {
            result = result.filter(s =>
                s.attributes.some(a => attrLabels.includes(a.label))
            );
        }

        return result;
    }, [stones, filter, searchFilters]);

    const skillStones = stones.filter(s => s.type === 'skill');
    const craftStones = stones.filter(s => s.type === 'craft');

    // 随机快捷添加一个机巧石
    const handleQuickAdd = useCallback(() => {
        const shapes: StoneShape[] = ['T', 'TIAN', 'L_LEFT', 'L_RIGHT', 'I'];
        const colors: StoneColor[] = ['white', 'green', 'blue', 'purple'];
        const elements: ElementType[] = ['fire', 'ice', 'wood', 'thunder'];
        const shape = shapes[Math.floor(Math.random() * shapes.length)];
        const color = colors[Math.floor(Math.random() * colors.length)];
        const element = elements[Math.floor(Math.random() * elements.length)];
        addStone('skill', shape, color, element);
    }, [addStone]);

    return (
        <div className="right-panel">
            {/* 筛选和操作 */}
            <div className="inventory-toolbar">
                <div className="filter-btns">
                    <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
                        全部 ({stones.length})
                    </button>
                    <button className={`filter-btn ${filter === 'skill' ? 'active' : ''}`} onClick={() => setFilter('skill')}>
                        机巧石 ({skillStones.length})
                    </button>
                    <button className={`filter-btn ${filter === 'craft' ? 'active' : ''}`} onClick={() => setFilter('craft')}>
                        匠心石 ({craftStones.length})
                    </button>
                </div>
                <div className="toolbar-actions">
                    <button className="btn btn-quick-add" onClick={handleQuickAdd} title="随机添加一个机巧石">
                        🎲 快捷
                    </button>
                    <button className="btn btn-add-stone" onClick={() => setShowEditor(true)}>
                        + 录入
                    </button>
                </div>
            </div>

            {/* 搜索按钮 */}
            <div className="search-bar">
                <button className={`search-btn ${hasActiveFilters ? 'has-filter' : ''}`} onClick={openSearch}>
                    🔍 搜索{hasActiveFilters ? `（${searchFilters.elements.length + searchFilters.types.length + searchFilters.colors.length + searchFilters.attrLabels.length}项）` : ''}
                    {hasActiveFilters && (
                        <span className="search-btn-badge">
                            {searchFilters.elements.length + searchFilters.types.length +
                             searchFilters.colors.length + searchFilters.attrLabels.length}
                        </span>
                    )}
                </button>
                {hasActiveFilters && (
                    <button className="search-clear" onClick={() => setSearchFilters({
                        elements: [], types: [], colors: [], attrLabels: [],
                    })} title="清除筛选条件">✕</button>
                )}
            </div>

            {/* 石头列表 */}
            <div className="stone-list">
                {filteredStones.length === 0 ? (
                    <div className="empty-hint">
                        {hasActiveFilters
                         ? '未找到匹配的石头'
                         : '暂无石头，点击「+ 录入」添加'}
                        <br />
                        <span className="hint-sub">机巧石：T形 / 田形 / 左L形 / 右L形</span>
                        <br />
                        <span className="hint-sub">匠心石：固定田形（2×2）</span>
                    </div>
                ) : (
                    filteredStones.map(stone => (
                        <StoneBlock
                            key={stone.id}
                            stone={stone}
                            onDragStart={handleDragStart}
                            onRemove={handleRemove}
                            onRotate={handleRotate}
                        />
                    ))
                )}
            </div>

            {/* 编辑器弹窗 */}
            {showEditor && <StoneEditor onClose={() => setShowEditor(false)} />}

            {/* 搜索弹窗 */}
            {showSearch && (
                <StoneSearchModal
                    stones={stones}
                    currentFilters={searchFilters}
                    onConfirm={confirmSearch}
                    onClose={cancelSearch}
                />
            )}
        </div>
    );
}